import React, { Dispatch, SetStateAction, useMemo } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import ActionButton, {
  ActionButtonSizes,
  ActionButtonVariants,
  ActionLink,
} from 'components/action-button';
import LoadingAnimation from 'components/loading-animation';
import { useOrderStatus } from 'hooks/order-status';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import LoadingIndicator from 'pages/store-locator/loading-indicator';
import { invalidStatusesWhileEnroute } from 'pages/support-v2/types';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { useReorder } from 'state/order/hooks/use-reorder';
import { DeliveryStatus } from 'state/order/types';
import { everyCartEntriesInMenu } from 'utils/cart';
import noop from 'utils/noop';

import { MODAL_TYPE } from '../types';

import { CartEntry } from './cart-entry';
import {
  Block,
  Container,
  Footer,
  LoadingContainer,
  ModalHead,
  ModalInner,
  NeedHelpButtonLoadingIndicatorWrapper,
  OrderDetailsScrollContainer,
  StyledCartEntries,
  StyledModal,
} from './details-modal.styled';
import getModalHeading from './get-modal-heading';
import IsEnRouteModal from './is-en-route-modal';
import { PointsEarnedBlock } from './points-earned';
import { ReceiptDetails } from './receipt-details';
import { ReceiptHeadInfo } from './receipt-head-info';
import { useHelpLinkUrlGenerator } from './use-help-link-url-generator';

// Receipt and order-status queries resolve independently. Never use a stale active
// delivery status to track an order whose payment/fulfillment has already failed.
const inactiveOrderStatuses = new Set([
  OrderStatus.REFUND_SUCCESSFUL,
  OrderStatus.REFUND_REQUESTED,
  OrderStatus.REFUND_ERROR,
  OrderStatus.INSERT_ERROR,
  OrderStatus.PAYMENT_ERROR,
  OrderStatus.PRICE_ERROR,
  OrderStatus.VALIDATION_ERROR,
  OrderStatus.CATERING_DECLINED,
  OrderStatus.CATERING_ERROR,
]);
const inactiveDeliveryStatuses = new Set([
  ...Array.from(invalidStatusesWhileEnroute),
  DeliveryStatus.ORDER_TIMEOUT,
  DeliveryStatus.ORDER_ERROR,
  DeliveryStatus.QUOTE_ERROR,
  DeliveryStatus.QUOTE_UNAVAILABLE,
]);

const DetailsModal: React.FC<{
  rbiOrderId: string;
  onModalDismiss: () => void;
  type: string;
  setModalType?: Dispatch<SetStateAction<MODAL_TYPE>>;
  setDetailsModalId?: (orderId: string) => void;
}> = ({ rbiOrderId, onModalDismiss, type, setModalType = noop, setDetailsModalId = noop }) => {
  const { formatMessage } = useIntl();
  const { order, loading: isOrderLoading, error: orderError } = useOrderDetails(rbiOrderId);

  const { handleReorderClick, disabled } = useReorder(order);
  const {
    reorder: { reordering },
  } = useOrderContext();

  const { isSupportDataLoading } = useSupportData();

  const helpLink = useHelpLinkUrlGenerator(rbiOrderId);
  const disableReorder = useMemo(
    () => disabled || !everyCartEntriesInMenu(order?.cart?.reorderCartEntries || []),
    [disabled, order]
  );

  const { serverOrder, loading: isStatusLoading, error: statusError } = useOrderStatus({
    failureStatuses: OrderSuccessFailureStatuses.failures,
    orderStatusPollInterval: 20000,
    rbiOrderId,
    successStatuses: OrderSuccessFailureStatuses.success,
    skip: false,
  });

  const statusOrder = serverOrder?.rbiOrderId === rbiOrderId ? serverOrder : null;
  const isDeliveryOrder = order?.cart.serviceMode === ServiceMode.DELIVERY;
  const receiptIsActive =
    isDeliveryOrder &&
    !!order?.delivery &&
    !inactiveOrderStatuses.has(order.status) &&
    !inactiveDeliveryStatuses.has(order.delivery.status);
  const statusIsActive =
    !!statusOrder?.delivery &&
    statusOrder.cart?.serviceMode === ServiceMode.DELIVERY &&
    !inactiveOrderStatuses.has(statusOrder.status) &&
    !inactiveDeliveryStatuses.has(statusOrder.delivery.status);
  const canTrackOrder =
    !isOrderLoading &&
    !orderError &&
    !isStatusLoading &&
    !statusError &&
    receiptIsActive &&
    statusIsActive;
  const statusIsTerminal =
    !!statusOrder &&
    (inactiveOrderStatuses.has(statusOrder.status) ||
      (statusOrder.delivery && inactiveDeliveryStatuses.has(statusOrder.delivery.status)));
  const isTrackingRequested =
    type !== MODAL_TYPE.SHOW_DETAILS && receiptIsActive && !statusIsTerminal;
  const isLoading = isOrderLoading || (isTrackingRequested && isStatusLoading);

  if (canTrackOrder && type !== MODAL_TYPE.SHOW_DETAILS) {
    return <IsEnRouteModal serverOrder={statusOrder} onModalDismiss={() => onModalDismiss()} />;
  }

  // Only a confirmed refund status may claim a refund; cancellation alone cannot.
  const isRefunded =
    isDeliveryOrder &&
    (order.status === OrderStatus.REFUND_SUCCESSFUL ||
      statusOrder?.status === OrderStatus.REFUND_SUCCESSFUL);
  const isCancelled =
    isDeliveryOrder &&
    (order.delivery?.status === DeliveryStatus.ORDER_CANCELLED ||
      statusOrder?.delivery?.status === DeliveryStatus.ORDER_CANCELLED);
  const hasTrackingError =
    isDeliveryOrder &&
    !isStatusLoading &&
    (statusError ||
      !statusOrder?.delivery ||
      inactiveOrderStatuses.has(statusOrder.status) ||
      (statusOrder.delivery &&
        inactiveDeliveryStatuses.has(statusOrder.delivery.status) &&
        statusOrder.delivery.status !== DeliveryStatus.ORDER_DROPPED_OFF) ||
      inactiveOrderStatuses.has(order.status) ||
      (order.delivery &&
        inactiveDeliveryStatuses.has(order.delivery.status) &&
        order.delivery.status !== DeliveryStatus.ORDER_DROPPED_OFF));
  const messageId = isRefunded
    ? 'supportFormOrderAlreadyRefunded'
    : isCancelled
    ? 'deliveryStatusOrderCancelled'
    : isDeliveryOrder &&
      (order.delivery?.status === DeliveryStatus.ORDER_TIMEOUT ||
        statusOrder?.delivery?.status === DeliveryStatus.ORDER_TIMEOUT)
    ? 'deliveryStatusOrderTimeout'
    : isDeliveryOrder &&
      (order.delivery?.status === DeliveryStatus.ORDER_ERROR ||
        statusOrder?.delivery?.status === DeliveryStatus.ORDER_ERROR)
    ? 'deliveryStatusOrderError'
    : orderError || !order || hasTrackingError
    ? 'errorLoadingUserOrders'
    : null;

  const loyaltyTransaction = order?.loyaltyTransaction;

  const handleTrackOrder = () => {
    setDetailsModalId(rbiOrderId);
    setModalType(MODAL_TYPE.TRACK_ORDER);
  };

  return (
    <StyledModal onDismiss={() => onModalDismiss()} data-testid="details-modal">
      <ModalInner>
        <ModalHead>
          <h2 id="modal-heading" data-testid="modal-heading">
            {getModalHeading(formatMessage)}
          </h2>
        </ModalHead>
        <OrderDetailsScrollContainer>
          <Container data-testid="modal-body">
            {isLoading ? (
              <LoadingContainer>
                <LoadingAnimation
                  fillColor={Styles.color.black}
                  ariaLabel={formatMessage({ id: 'loading' })}
                />
              </LoadingContainer>
            ) : (
              <>
                {messageId && <p role="status">{formatMessage({ id: messageId })}</p>}
                {order && !orderError && (
                  <>
                    <Block>
                      <ReceiptHeadInfo order={order} />
                    </Block>
                    {loyaltyTransaction && (
                      <PointsEarnedBlock lylTransaction={loyaltyTransaction} />
                    )}
                    <StyledCartEntries>
                      {order.cart.cartEntries.map((entry, i) => (
                        <CartEntry entry={entry} key={entry.name! + i} index={i} />
                      ))}
                    </StyledCartEntries>
                    <Block>
                      <ReceiptDetails order={order} />
                    </Block>
                  </>
                )}
              </>
            )}
          </Container>
        </OrderDetailsScrollContainer>
        <Footer data-testid="modal-footer">
          <Container>
            {order &&
              !orderError &&
              (canTrackOrder || (receiptIsActive && isStatusLoading && !statusIsTerminal) ? (
                <ActionButton
                  fullWidth
                  onClick={handleTrackOrder}
                  isLoading={isStatusLoading}
                  disabled={!canTrackOrder}
                >
                  {formatMessage({ id: 'trackOrder' })}
                </ActionButton>
              ) : (
                <ActionButton
                  fullWidth
                  onClick={() => {
                    handleReorderClick();
                    onModalDismiss();
                  }}
                  isLoading={reordering}
                  disabled={disableReorder}
                >
                  {formatMessage({ id: 'reorder' })}
                </ActionButton>
              ))}
            <p>
              {formatMessage({
                id: 'pricesDiscountsAndAvailabilityAreSbujectToChangeWhenYouReorder',
              })}
            </p>

            {isSupportDataLoading && (
              <NeedHelpButtonLoadingIndicatorWrapper>
                <LoadingIndicator />
              </NeedHelpButtonLoadingIndicatorWrapper>
            )}

            {/*
             * isSupportDataLoading becomes false quickly, so aria-live="polite" on this button is distracting.
             * This will silently appear for screen readers.
             */}
            {!isSupportDataLoading && (
              <Box margin="auto">
                <ActionLink
                  data-testid="recent-order-need-help"
                  fullWidth
                  variant={ActionButtonVariants.OUTLINE}
                  size={ActionButtonSizes.SMALL}
                  to={helpLink}
                >
                  {formatMessage({ id: 'needHelp' })}
                </ActionLink>
              </Box>
            )}
          </Container>
        </Footer>
      </ModalInner>
    </StyledModal>
  );
};

export default DetailsModal;
